import React from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Autocomplete,
  Paper,
} from "@mui/material";
import { temp, ui } from "../../interfaces/interfaces";
import useDebugInformation from "@hooks/useDebugInformation";
import { FormAddress, AddressFields, AddressPrefix } from "@utils/BasicTypes";
import { AddressUtils } from "@utils/address";
import { AddressFromAPI } from "@utils/address/model";
import { useAppSelector } from "@store/hooks";

type Props = {
  prefix: AddressPrefix;
};

const AddressModal: React.FC<Props> = ({ prefix }) => {
  const [mergedAddresses, setMergedAddresses] = React.useState<AddressFromAPI[]>([]);
  const modalOpen = useAppSelector(({ ui }) => ui[`${prefix}ModalOpen`]);
  const addressToMatch = useAppSelector(({ ui }) => ui.AddressToMatch);
  const selectedAddress = useAppSelector(({ ui }) => ui[`${prefix}SelectedAddress`]);

  React.useEffect(() => {
    if (modalOpen) {
      fetchMergedAddresses(prefix, setMergedAddresses);
    }
  }, [modalOpen, prefix]);

  useDebugInformation("AddressModalOpen", { modalOpen, selectedAddress, mergedAddresses });

  const handleClose = React.useCallback(() => {
    ui[`${prefix}ModalOpen`] = false;
    ui.AddressToMatch = "";
  }, [prefix]);

  const handleSave = React.useCallback(() => {
    saveSelectedAddress(selectedAddress, prefix);
  }, [prefix, selectedAddress]);

  return (
    <Dialog open={modalOpen && mergedAddresses.length > 0} onClose={handleClose} aria-labelledby="form-dialog-title" maxWidth="xl">
      <DialogTitle id="form-dialog-title">Addresses found by Postcode</DialogTitle>
      <DialogContent dividers>
        <DialogContentText>Select a valid address and click save or cancel to enter manually.</DialogContentText>
        {addressToMatch && (
          <>
            Address to match: <Paper sx={{ p: 1 }}>{addressToMatch}</Paper>
          </>
        )}
        <Paper elevation={4}>
          {mergedAddresses.length > 0 && (
            <Autocomplete
              id="combo-box-addresses"
              options={mergedAddresses
                .sort((a, b) => a.addressString?.localeCompare(b.addressString || "") || 0)
                .sort((a, b) => b.scope?.localeCompare(a.scope || "") || 0)}
              getOptionLabel={(option) => option.addressString || ""}
              onChange={(event, value) => handleAddressChange(value, prefix)}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              groupBy={(option) => option.scope || ""}
              value={selectedAddress || null}
              renderInput={(params) => <TextField {...params} label="Address list" variant="outlined" autoFocus />}
              sx={{ mt: 2 }}
            />
          )}
        </Paper>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="secondary">
          Cancel
        </Button>
        <Button onClick={handleSave} color="primary">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const saveSelectedAddress = (selectedAddress: AddressFromAPI | undefined, prefix: AddressPrefix) => {
  if (selectedAddress) {    
    const formAddress = AddressUtils.mapAddressResourceToFormAddress(selectedAddress);        
    for (const key in formAddress) {      
      const field = formAddress[key as keyof FormAddress];
      ui[`${prefix}${key as AddressFields}`] = field || "";
    }
    ui[`${prefix}IsBusiness`] = Boolean(formAddress.BusinessName);
    if (prefix === "PropertyAddress" && selectedAddress.addressString) {
      ui.ProblemAddressString = selectedAddress.addressString;
    }
    ui[`${prefix}ModalOpen`] = false;
    ui.AddressToMatch = "";
  }
};

const handleAddressChange = (selectedAddress: AddressFromAPI | null, prefix: AddressPrefix) => {
  ui[`${prefix}SelectedAddress`] = selectedAddress || undefined;
  saveSelectedAddress(selectedAddress || undefined, prefix);
};

const fetchMergedAddresses = async (
  prefix: AddressPrefix,
  setMergedAddresses: React.Dispatch<React.SetStateAction<AddressFromAPI[]>>
) => {
  const mergedAddresses = await AddressUtils.mergeAddresses({ prefix, temp, ui });
  setMergedAddresses(mergedAddresses);
};

export default React.memo(AddressModal);
